import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import { IoClose, IoOpenOutline } from "react-icons/io5";

import WOService from "src/Services/API/wo.service";
import { getAssignedTechsNameArray } from "src/Common/function";
import { priorityLevels } from "src/Common/contants";
import WOStatusComponent from "src/Pages/WO/Components/Status/wostatuscomponent";
import WoType from "src/Pages/WO/Components/Types/WoType";

/**
 * A quick look at a work order without leaving the payroll screen: the fields
 * the WO table shows in a row, laid out to be read at a glance. Not a second
 * detail page — a link at the bottom goes there for anything more.
 *
 * Two requests: the breakdown row carries only the WO number, and the
 * by-number lookup returns only id and number (its one other caller needs
 * nothing else), so the full record is fetched by id once the id is known.
 */
const Field = ({ label, children, wide = false }) => (
  <div className={wide ? "col-span-2" : ""}>
    <div className="font-OpenSansRegular text-[11px] font-[600] uppercase tracking-[0.6px] text-[#98A2B3]">
      {label}
    </div>
    <div className="mt-0.5 font-OpenSansRegular text-[14px] leading-[20px] text-[#1A2332] break-words">
      {children ?? <span className="text-[#B9C0CF]">—</span>}
    </div>
  </div>
);

const WoGlanceModal = ({ woNumber, onClose }) => {
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | idle | error
  // What went wrong, said specifically: which call, what status, what the
  // server said. A generic "could not be loaded" hides exactly the thing
  // needed to fix it.
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (!woNumber) return undefined;
    let cancelled = false;
    setStatus("loading");
    setOrder(null);
    setFailure(null);
    let step = "looking up the number";
    WOService.getWorkOrderByNumber(woNumber)
      .then((byNumber) => {
        const id = byNumber?.data?.id;
        if (!id) {
          const found = byNumber?.data;
          throw new Error(
            `No work order numbered ${woNumber} in this company (lookup returned ${
              found == null ? "nothing" : JSON.stringify(found)
            })`
          );
        }
        step = `loading work order ${id}`;
        return WOService.getWorkOrderById(id);
      })
      .then((response) => {
        if (cancelled) return;
        setOrder(response.data);
        setStatus("idle");
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("WoGlanceModal:", step, error);
        if (cancelled) return;
        const httpStatus = error?.response?.status;
        const serverMessage =
          error?.response?.data?.message || error?.response?.data?.error || error?.message;
        setFailure(
          `Failed while ${step}${httpStatus ? ` (HTTP ${httpStatus})` : ""}${
            serverMessage ? `: ${String(serverMessage)}` : ""
          }`
        );
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [woNumber]);

  if (!woNumber) return null;

  // Despite its name this returns a joined string ("Ann, Bob"), or a "waiting"
  // phrase when nobody is assigned — and it reorders its input in place to put
  // the primary technician first, so it gets a copy rather than the order's
  // own array.
  const techs = order ? getAssignedTechsNameArray([...(order.assignedTechs || [])]) : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <OutsideClickHandler onOutsideClick={onClose}>
        <div className="w-[560px] max-w-[calc(100vw-32px)] max-h-[90vh] flex flex-col bg-white rounded-[12px] overflow-hidden">
          <div className="px-6 py-5 border-b border-[#E5E8F3] flex items-start justify-between">
            <div className="flex flex-col gap-1 min-w-0">
              <h2 className="font-OpenSansSemiBold text-[20px] leading-[28px] text-[#1A2332]">
                WO-{woNumber}
              </h2>
              {order ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <WOStatusComponent value={order.status} />
                  {order.serviceType ? <WoType serviceType={order.serviceType} /> : null}
                </div>
              ) : null}
            </div>
            <button type="button" onClick={onClose} className="text-[#868DA6] cursor-pointer" aria-label="Close">
              <IoClose size={22} />
            </button>
          </div>

          <div className="px-6 py-5 overflow-y-auto">
            {status === "loading" ? (
              <p className="font-OpenSansRegular text-[13px] text-[#98A2B3]">Loading…</p>
            ) : status === "error" ? (
              <p className="font-OpenSansRegular text-[13px] text-[#B91C1C] break-words">
                {failure || "This work order could not be loaded."}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <Field label="Customer">{order.customer?.companyName}</Field>
                <Field label="Branch">{order.branch?.name}</Field>
                <Field label="Start date">{order.startDateString || null}</Field>
                <Field label="Priority">
                  {order.priorityLevel != null ? priorityLevels[order.priorityLevel] : null}
                </Field>
                <Field label="NTE">{order.NTE != null && order.NTE !== "" ? order.NTE : null}</Field>
                <Field label="Assigned technicians">{techs || null}</Field>
                <Field label="Description" wide>
                  {order.description ? (
                    <span className="whitespace-pre-line">{order.description}</span>
                  ) : null}
                </Field>
              </div>
            )}
          </div>

          {order ? (
            <div className="px-6 py-4 border-t border-[#E5E8F3] flex justify-end">
              <Link
                to={`/work/${order.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-OpenSansSemiBold text-[13px] text-[#0065FD] hover:underline"
              >
                Open work order
                <IoOpenOutline size={14} />
              </Link>
            </div>
          ) : null}
        </div>
      </OutsideClickHandler>
    </div>
  );
};

export default WoGlanceModal;
